import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Card } from "../../components/ui/card";
import type { DataTableProps, DataTableColumn } from "../../../types/admin";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

export function DataTable<T = any>({
  columns,
  data,
  onRowClick,
  loading = false,
  emptyMessage = "No data available",
}: DataTableProps<T>) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (columnKey: string) => {
    if (!columns.find((col) => col.key === columnKey)?.sortable) return;

    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (aValue === bValue) return 0;

      const comparison = aValue > bValue ? 1 : -1;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [data, sortColumn, sortDirection]);

  if (loading) {
    return (
      <Card className="rounded-xl border-0 bg-sand p-8 shadow-card dark:bg-darkcard">
        <div className="flex h-[300px] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-coral border-t-transparent" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border-0 bg-sand shadow-card dark:bg-darkcard">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-martinique/10 dark:border-white/10">
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={`text-martinique/70 dark:text-white/70 ${
                    column.sortable ? "cursor-pointer select-none" : ""
                  } ${column.className || ""}`}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{column.label}</span>
                    {column.sortable && (
                      <span className="text-martinique/40 dark:text-white/40">
                        {sortColumn === column.key ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4" />
                        )}
                      </span>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-[200px] text-center text-martinique/50 dark:text-white/50"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  className={`border-b border-martinique/5 dark:border-white/5 ${
                    onRowClick
                      ? "cursor-pointer hover:bg-martinique/5 dark:hover:bg-white/5"
                      : ""
                  }`}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={`text-martinique dark:text-white ${column.className || ""}`}
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : String(row[column.key] || "")}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
